// Central call-activity writer. EVERY call logged anywhere in the app should go
// through this so the Telecaller Workspace sees one unified stream.
//
// Writes BOTH:
//   1. callActivities/{callId}                     (global stream — workspace)
//   2. customerBase/{key}/callHistory/{callId}     (per-customer — profile)
// using the SAME id and the last-10-digit customer key (matches existing data).
import { ref, push, update } from 'firebase/database';
import { db } from '../../integrations/firebase';

const keyOf = (m) => String(m || '').replace(/\D/g, '').slice(-10);
const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== ''));

export const SOURCE_MODULES = [
    'customer_profile', 'all_orders', 'pre_shipment', 'complaints',
    'doctor', 'tracking', 'feedback', 'lost_customer', 'manual',
    'ekart', 'delivery_followup',
];

// Ekart / delivery call types — flow into the unified callActivities stream.
export const EKART_CALL_TYPES = [
    'delivery_followup', 'shipment_confirmation', 'address_confirmation',
    'ndr_followup', 'delivery_delay', 'customer_not_available',
    'rto_confirmation', 'reship_confirmation', 'refund_confirmation',
];

// Server-stamped logging: the proxy writes the activity with the VERIFIED
// token identity (calledBy/calledByUid can't be spoofed), creates the
// callback atomically when a follow-up is set, and keeps the customer
// index's updatedAt honest. Falls back to the legacy client write only if
// the endpoint is unreachable, so a proxy blip never loses a call log.
const serverLog = async (payload, key) => {
    const PROXY = (import.meta.env?.VITE_PROXY_URL || (import.meta.env?.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');
    const { authHeaders } = await import('../api/authHeaders');
    const r = await fetch(`${PROXY}/api/tc/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
            mobile: key, customerName: payload.customerName, orderId: payload.orderId,
            ticketId: payload.ticketId, sourceModule: payload.sourceModule, callType: payload.callType,
            result: payload.result, remark: payload.remark,
            outcomeOnly: !!payload.outcomeOnly, sessionId: payload.sessionId || '',
            calledAt: payload.calledAt,
            assignToUid: payload.assignToUid || '',
            followUpAt: payload.followUpAt
                ? (typeof payload.followUpAt === 'number' ? payload.followUpAt : new Date(payload.followUpAt).getTime())
                : 0,
        }),
    });
    const j = await r.json();
    if (!r.ok || !j.ok) throw new Error(j.error || `log failed (${r.status})`);
    return j.callId;
};

export const CallActivityService = {
    // Log one call. Returns the generated callId.
    logCall: async (payload = {}) => {
        const key = payload.customerKey || keyOf(payload.mobile);
        // Server-only: identity is stamped from the verified token and the
        // callback is created atomically. No silent client-write fallback —
        // if the server is unreachable the caller is TOLD, not misled.
        return await serverLog(payload, key || keyOf(payload.mobile));
        // (The legacy client write below is kept only for reference during
        // the migration window and is unreachable.)
        const id = push(ref(db, 'callActivities')).key;
        const at = payload.calledAt || Date.now();
        const followUpMs = payload.followUpAt
            ? (typeof payload.followUpAt === 'number' ? payload.followUpAt : new Date(payload.followUpAt).getTime())
            : 0;

        const activity = clean({
            callId: id,
            customerKey: key || '',
            customerName: payload.customerName || '',
            mobile: keyOf(payload.mobile),
            orderId: payload.orderId || '',
            ticketId: payload.ticketId || '',
            sourceModule: payload.sourceModule || 'manual',
            callType: payload.callType || '',
            result: payload.result || '',
            remark: payload.remark || '',
            productDiscussed: payload.productDiscussed || '',
            offerDiscussed: payload.offerDiscussed || '',
            customerMood: payload.customerMood || '',
            calledBy: payload.calledBy || '',
            calledByUid: payload.calledByUid || '',
            calledByRole: payload.calledByRole || '',
            calledAt: at,
            followUpAt: followUpMs || undefined,
            assignedTo: payload.assignedTo || '',
            convertedOrderId: payload.convertedOrderId || '',
            convertedRevenue: payload.convertedRevenue || undefined,
            createdAt: Date.now(),
        });

        const updates = { [`callActivities/${id}`]: activity };
        if (key) {
            // Keep the server's incremental customer index honest.
            updates[`customerBase/${key}/updatedAt`] = Date.now();
            updates[`customerBase/${key}/callHistory/${id}`] = clean({
                callType: activity.callType, result: activity.result, remark: activity.remark,
                productDiscussed: activity.productDiscussed, offerDiscussed: activity.offerDiscussed,
                customerMood: activity.customerMood, calledBy: activity.calledBy, calledAt: at,
                followUpAt: followUpMs || undefined, assignedTo: activity.assignedTo,
                sourceModule: activity.sourceModule, callActivityId: id,
            });
        }
        await update(ref(db), updates);
        return id;
    },

    // Link a call to the order it converted into (revenue tracking).
    linkConversion: async (callId, customerKey, orderId, revenue) => {
        if (!callId) return;
        const updates = {
            [`callActivities/${callId}/convertedOrderId`]: orderId || '',
            [`callActivities/${callId}/convertedRevenue`]: Number(revenue) || 0,
        };
        if (customerKey) updates[`customerBase/${customerKey}/callHistory/${callId}/convertedOrderId`] = orderId || '';
        await update(ref(db), updates);
    },
};

export default CallActivityService;
